/* eslint-disable import/no-cycle */
import usePricings from '@cont/Checkout/usePricings';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { subscriptionTypes } from '@constants/index';
import Image from '@base/FutureImage';
import useFetch from '@hooks/useFetch';
import usePurchaseDetails from '@cont/Checkout/usePurchaseDetails';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToIconic,
  getIsSubscribedToIconicPro
} from '@api/selectors/subscription';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import styled from '@emotion/styled';
import Typography from 'aquilla/core/atoms/Typography';
import ExtendPlanSelectionPage from './ExtendPlanSelectionPage';
import {
  HeaderWrapper,
  TopWrapper,
  StyLeftChevron,
  HelpWrapper
} from '../PlanSelection';

const Title = styled(Typography)`
  width: 300px;
`;

const ExtendPlanSelection = ({ setIsHelpDrawerOpen }) => {
  const [preFilteredSubscriptionLists, setPreFilteredSubscriptionLists] =
    useState<Record<string, any> | undefined>();
  const [referralCode, setReferralCode] = useState<string>();
  const [selectedPlan, setSelectedPlan] = useState<
    Record<string, any> | undefined
  >();
  const { query: { goalUID = '' } = {} } = useRouter();
  const { pricings } = usePricings({ goalUID });
  const { goal = {} } = pricings ?? {};
  const isIconicUser = useMyInfo(getIsSubscribedToIconic(goalUID));
  const isIconicProUser = useMyInfo(getIsSubscribedToIconicPro(goalUID));

  const extendPlanType = useMemo(() => {
    if (isIconicProUser) return subscriptionTypes.ICONIC_PRO;
    if (isIconicUser) return subscriptionTypes.ICONIC;
    return subscriptionTypes.PLUS;
  }, [isIconicUser, isIconicProUser]);

  const {
    purchaseDetails,
    purchaseDetailsWithoutDiscount,
    referral_code,
    validateReferralInProgress,
    referralError,
    isError
  } = usePurchaseDetails({ subscriptionUid: selectedPlan?.uid, referralCode });

  const {
    data: discountedPricings,
    isError: referralAllError,
    isLoading: referralAllLoading
  } = useFetch(
    referral_code && goal?.uid
      ? `v2/subscriptions/validate_referral_all_subscriptions/?referral=${encodeURIComponent(
          referralCode
        )}&goal=${goal?.uid}&iconic=${
          goal?.is_iconic_available ?? false
        }&upgrade=true&scheme=2&iconic_pro=${
          goal?.is_iconic_pro_available ?? false
        }`
      : null,
    { useCamelCase: false },
    { shouldRetryOnError: false }
  );

  useEffect(() => {
    const preFilteredSubscriptionLists = pricings?.subscriptions
      ? [...pricings.subscriptions].filter(
          (pricing) => extendPlanType === pricing.type
        )
      : [];

    setPreFilteredSubscriptionLists(preFilteredSubscriptionLists);
  }, [extendPlanType, pricings]);

  const preFilteredExtendedSubscriptionLists = useMemo(() => {
    const allowedPreFilteredSubscriptionLists =
      preFilteredSubscriptionLists?.filter((plan) => {
        return !!plan?.is_purchase_allowed;
      });
    return allowedPreFilteredSubscriptionLists?.reverse() ?? [];
  }, [preFilteredSubscriptionLists]);

  const discountedPricingLists = useMemo(() => {
    return discountedPricings
      ? discountedPricings.reduce((acc, pricing) => {
          const newPricing = {
            ...pricing?.subscription,
            referral_details: { ...pricing?.referral }
          };
          if (pricing?.subscription?.type === extendPlanType) {
            acc.push(newPricing);
          }
          return acc;
        }, [])
      : [];
  }, [discountedPricings, extendPlanType]);

  const referralValid = !referralAllLoading && !referralAllError;

  useEffect(() => {
    if (referralCode && referralValid)
      setPreFilteredSubscriptionLists(discountedPricingLists);
  }, [
    discountedPricingLists,
    referralCode,
    referralValid,
    referralAllError,
    referralAllLoading
  ]);

  return (
    <>
      <HeaderWrapper>
        <TopWrapper $showBackButton={false}>
          <Title variant="h2">Extend duration for your {goal?.name} prep</Title>
          <HelpWrapper
            onClick={() => {
              setIsHelpDrawerOpen(true);
            }}
            $showBackButton={false}
          >
            <Image
              src="newPlanSelection/callfilledIcon.svg"
              width={16}
              height={16}
              alt="call icon"
            />
            Help
          </HelpWrapper>
        </TopWrapper>
      </HeaderWrapper>
      {preFilteredExtendedSubscriptionLists && (
        <ExtendPlanSelectionPage
          goalDetails={goal}
          preFilteredSubscriptionLists={preFilteredExtendedSubscriptionLists}
          setReferralCode={setReferralCode}
          referralCode={referralCode}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          purchaseDetails={purchaseDetails}
          referralError={referralError}
          validateReferralInProgress={validateReferralInProgress}
          referralValid={referralValid}
          referralAllLoading={referralAllLoading}
          discountedPricings={discountedPricings}
        />
      )}
    </>
  );
};

export default ExtendPlanSelection;
